import { Suspense } from 'react'
import { Link } from 'react-router-dom'

import { useMarkNotificationAsRead } from 'src/features/notifications/api/use-mark-notifications-as-read'
import { useUserByIdSuspense } from 'src/shared/features/users/api/accounts/use-user-by-id'
import type { NotificationActivity, NotificationGroup } from 'src/shared/schemas/stream/notification'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import { useNotificationContent } from '../../hooks/use-notification-content'
import { NotificationSidebarItemSkeleton } from '../notification-skeleton/notification-sidebar-skeleton'
interface NotificationSidebarItemProps {
  activity: NotificationActivity
  group: NotificationGroup
}

const NotificationSidebarItemContent = ({ activity, group }: NotificationSidebarItemProps) => {
  const markAsRead = useMarkNotificationAsRead()
  const firstActorId = activity.actor.split(':')[1]
  const { data: firstUser } = useUserByIdSuspense(Number.parseInt(firstActorId))
  const { text, avatar, symbol, link } = useNotificationContent(activity, group, firstUser)

  const handleClick = async () => {
    if (!group.isRead) {
      try {
        await markAsRead.mutateAsync(group.id)
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    }
  }

  return (
    <Link to={link} onClick={handleClick}>
      <div className={cn('w-full px-5 py-3 transition-colors hover:bg-accent/50', !group.isRead && 'bg-secondary/5')}>
        <div className="flex items-start gap-3">
          <div className="relative">
            {!group.isRead && (
              <div className="absolute -left-3 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-secondary" />
            )}
            <div className="relative">
              {avatar}
              {symbol}
            </div>
          </div>
          <div className="flex flex-col items-start justify-between gap-1">
            <p className="line-clamp-2 text-sm">{text}</p>
            <span className="text-xs text-muted">{formatMessageTime(activity.time, { short: true })}</span>
          </div>
        </div>
      </div>
    </Link>
  )
}

export const NotificationSidebarItem = ({ activity, group }: NotificationSidebarItemProps) => {
  return (
    <Suspense fallback={<NotificationSidebarItemSkeleton />}>
      <NotificationSidebarItemContent activity={activity} group={group} />
    </Suspense>
  )
}
